import { PlatewellProvider } from '@platewell/query';
import {
  createHttpRevisionBackend,
  HouseholdManager,
  IndexedDbUpdateLog,
} from '@platewell/sync-client';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { inviteTokenForPath, SignIn } from './auth.js';
import { ImpersonationBar } from './components/impersonation-bar.js';
import { API_BASE_URL, cachedSession, fetchSession, type Session } from './lib/api.js';
import { initSentry, reportError, rootErrorHandlers, setSentryUser } from './lib/sentry.js';
import { SessionProvider } from './lib/session.js';
import * as billing from './lib/billing.js';
import { uploadQueue } from './lib/uploads.js';
import { ResetPassword } from './reset-password.js';
import { router } from './router.js';
import './styles.css';

function Root() {
  // Starting from the cached session means a returning user sees their recipes immediately
  // instead of a spinner, and sees them at all when there is no network to ask.
  const [session, setSession] = useState<Session | null | undefined>(
    () => cachedSession() ?? undefined,
  );
  // Distinguishes a session the server has confirmed from one restored out of the cache.
  // Only the confirmed one can be trusted to say what a user may *not* do.
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchSession().then((value) => {
      if (cancelled) return;
      setSession(value);
      setVerified(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (session === undefined) return <div className="boot">Loading Platewell…</div>;
  if (session === null) {
    if (window.location.pathname === '/reset-password') {
      return (
        <ResetPassword
          onDone={() => {
            window.location.assign('/login');
          }}
        />
      );
    }
    // The signed-out screen owns invite links too: it stays on the invite's path, so the
    // router accepts the invite as soon as the new session renders it.
    return (
      <SignIn
        onSignedIn={setSession}
        inviteToken={inviteTokenForPath(window.location.pathname)}
      />
    );
  }
  // Keyed so signing in as someone else rebuilds the manager instead of reusing sockets and
  // documents opened for the previous user.
  // `verified` is a prop rather than part of the key: confirming the session must not tear
  // down the household manager and its live sockets.
  return <SignedIn key={session.user.id} session={session} verified={verified} />;
}

function SignedIn({ session, verified }: { session: Session; verified: boolean }) {
  // The RevenueCat App User ID is the Platewell user id, never an anonymous one: an
  // anonymous subscriber cannot be recovered after a browser change or on a second device,
  // and the webhook would have nothing to bind a purchase to.
  useEffect(() => {
    billing.signIn({
      id: session.user.id,
      name: session.user.name,
      email: session.user.email,
    });
  }, [session.user.id]);

  useEffect(() => {
    setSentryUser(session.user.id);
    return () => setSentryUser(null);
  }, [session.user.id]);

  // Built once per signed-in user: the manager owns every open household document, so
  // rebuilding it on a render would drop live sockets and re-parse every document.
  const [manager] = useState(
    () =>
      new HouseholdManager({
        userId: session.user.id,
        apiBaseUrl: API_BASE_URL,
        storage: window.localStorage,
        createUpdateLog: (documentId) => new IndexedDbUpdateLog(documentId),
        revisions: (householdId) => createHttpRevisionBackend(API_BASE_URL, householdId),
        onPersistenceError: (error) => reportError('persistence', error),
      }),
  );

  useEffect(() => {
    // Photos captured offline sit in the queue until the browser says the network is back,
    // and the sync sockets follow the same signal so the indicator is honest right away.
    const goOnline = () => {
      manager.setNetworkOnline(true);
      void uploadQueue.drain();
    };
    const goOffline = () => manager.setNetworkOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    manager.setNetworkOnline(navigator.onLine);
    if (navigator.onLine) void uploadQueue.drain();

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      void manager.closeAll();
    };
  }, [manager]);

  return (
    <PlatewellProvider manager={manager} uploads={uploadQueue} apiBaseUrl={API_BASE_URL}>
      <SessionProvider session={session} verified={verified}>
        {/* Above the router, not inside it: an admin acting as someone else must be told
            so on every screen, including the ones the app shell never renders — the boot
            state, the lock screen and the support console itself. */}
        <ImpersonationBar />
        <RouterProvider router={router} />
      </SessionProvider>
    </PlatewellProvider>
  );
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Without the worker the app still runs, it just cannot boot offline. Registration is
    // refused by private windows, blocked storage and some extensions, none of which is ours to fix.
    navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      console.warn('Service worker registration failed', error);
    });
  });
}

// Before the first render, so an error while the app boots is reported too.
initSentry(router);

createRoot(document.getElementById('root')!, rootErrorHandlers).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
